import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useLocale } from "@/hooks/useLocale";
import { localizedPath } from "@/i18n/languages";
import { MAIN_SITE_URL, SITE_URL } from "@/constants";
import { articlesMeta } from "@/mocks/articles";

const SECTION_KEYS = [
  { id: "s1", key: "s1" },
  { id: "s2", key: "s2" },
  { id: "s3", key: "s3" },
];

const FAQ_KEYS = [
  { id: "f1", key: "f1" },
  { id: "f2", key: "f2" },
];

export default function Article() {
  const { t } = useTranslation();
  const lang = useLocale();
  const location = useLocation();

  const pathParts = location.pathname.split("/").filter(Boolean);
  const rawSlug = pathParts[pathParts.length - 1] ?? "";
  const meta = articlesMeta.find((a) => a.slug === rawSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [rawSlug]);

  if (!meta) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-file-search-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            {t("article.notFound")}
          </h1>
          <Link
            to={localizedPath(lang, "/")}
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("article.backHome")}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const homeHref = localizedPath(lang, "/");
  const slug = rawSlug;
  const title = t(`art.${slug}.title`);
  const metaDescription = t(`art.${slug}.meta`);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: metaDescription,
    image: meta.image,
    url: `${SITE_URL}${location.pathname}`,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}${location.pathname}`,
    },
    author: { "@type": "Organization", name: t("article.author.name") },
    datePublished: "2026-08-18",
    dateModified: "2026-08-18",
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Hero banner */}
        <section className="relative flex min-h-[300px] items-end overflow-hidden md:min-h-[360px]">
          <img
            src={meta.image}
            alt={title}
            title={title}
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/55" />
          <div className="relative z-10 w-full px-4 pb-8 md:px-6">
            <div className="mx-auto max-w-4xl">
              <nav className="mb-4 flex flex-wrap items-center gap-2 text-xs text-white/80">
                <Link to={homeHref} className="hover:text-white">
                  {t("home.breadcrumb")}
                </Link>
                <span className="text-white/50">/</span>
                <span className="text-white">{t(`art.${slug}.badge`)}</span>
              </nav>
              <span className="inline-flex items-center rounded-full border border-white/25 bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                {t(`art.${slug}.badge`)}
              </span>
            </div>
          </div>
        </section>

        {/* Article body */}
        <article className="mx-auto max-w-4xl px-4 py-12 md:px-6 md:py-16">
          <h1 className="font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-5xl">
            {title}
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-background-200 pb-6 text-xs text-foreground-500">
            <span>
              {t("article.author")}{" "}
              <span className="font-medium text-foreground-800">{t("article.author.name")}</span>
            </span>
            <span>{t("article.published")} {t("article.date")}</span>
            <span>{t("article.updated")} {t("article.date")}</span>
          </div>

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

          <p className="mt-8 text-base leading-relaxed text-foreground-700 md:text-lg">
            {t(`art.${slug}.intro`)}
          </p>

          {SECTION_KEYS.map((section) => {
            const h2 = t(`art.${slug}.${section.key}.h2`);
            const p1 = t(`art.${slug}.${section.key}.p1`);
            const p2 = t(`art.${slug}.${section.key}.p2`);
            if (!h2) return null;
            return (
              <section key={section.id} id={section.id} className="mt-12">
                <h2 className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
                  {h2}
                </h2>
                {p1 && (
                  <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                    {p1}
                  </p>
                )}
                {p2 && (
                  <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                    {p2}
                  </p>
                )}
              </section>
            );
          })}

          {/* FAQ */}
          <section id="faq" className="mt-12">
            <h2 className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              {t("article.faqHeading")}
            </h2>
            <div className="mt-6 space-y-5">
              {FAQ_KEYS.map((faq) => {
                const q = t(`art.${slug}.${faq.key}.q`);
                const a = t(`art.${slug}.${faq.key}.a`);
                if (!q) return null;
                return (
                  <div key={faq.id}>
                    <h3 className="text-base font-semibold text-foreground-900">{q}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{a}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Conclusion */}
          <section id="conclusion" className="mt-12">
            <h2 className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              {t("article.conclusionHeading")}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              {t(`art.${slug}.conclusion`)}
            </p>
          </section>

          {/* CTA */}
          <div className="mt-12 rounded-lg bg-primary-600 p-6 text-center md:p-8">
            <p className="text-base font-medium text-white md:text-lg">{t("article.cta.text")}</p>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-white"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-shopping-bag-line text-base" />
              </span>
              {t("article.cta.button")}
            </a>
          </div>

          <div className="mt-8 flex items-center justify-between border-t border-background-200 pt-6">
            <Link
              to={homeHref}
              className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-primary-600 hover:text-primary-700"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-left-line text-base" />
              </span>
              {t("article.backHome")}
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}